// src/pages/mc/NewDemandDialog.tsx
//
// Correção UX v2 (PRINCÍPIO ZERO) — modal "Nova demanda" LITERAL do wireframe
// (dialog #dlgNewDemand, class "wide"): título, negócio + prioridade (2col),
// objetivo, descrição, dentro/fora do escopo (2col), critérios de aceite (um
// por linha), runtime + supervisão + pipeline (3col). Trigger = botão
// .dnewbtn fechando a linha de filtros à direita (C1).
//
// POST /v1/demands via api.createDemand. O backend (CreateDemandSchema)
// aceita TODOS os campos do wireframe: objective, scope_in, scope_out,
// acceptance_criteria, runtime_target, supervision_mode, pipeline_slug,
// business_id. A assinatura TS de api.createDemand não declara os campos
// extras (api.ts é intocável nesta frente) — eles entram via spread tipado e
// o método repassa `...rest` no body.
//
// metadata.creator_context é OBRIGATÓRIO no backend (CHECK constraint
// demands_creator_context_canonical) mas o wireframe NÃO tem esse campo —
// derivamos do próprio form (origem UI + objetivo/título).

import { useEffect, useState } from 'react';
import { ClipboardList, Plus, X } from 'lucide-react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { api, type Business, type DemandPriority } from '@/lib/api';
import { listPipelines, type PipelineSummary } from '@/lib/api-pipelines';
import {
  NEW_DEMAND_PRIORITY_OPTIONS,
  NEW_DEMAND_RUNTIME_OPTIONS,
  NEW_DEMAND_SUPERVISION_OPTIONS,
} from './demand-constants';
// DEM-2010 AC#2: a UI valida os campos contra o contrato e deriva um PREVIEW
// dos campos faltantes via a MESMA `deriveDemandFields` (espelho da fonte única
// do backend), SEM obrigar preenchimento manual — a submissão só bloqueia em
// violações REAIS (ex: business_id ausente quando o pipeline exige).
import { previewDerivedFields } from '@/lib/demand-contract';

import '@/styles/wf-demands.css';

interface NewDemandDialogProps {
  /** Chamado após criar com sucesso (page refaz fetch da lista). */
  onCreated?: () => void;
}

export function NewDemandDialog({ onCreated }: NewDemandDialogProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Campos do wireframe (dlgNewDemand), na mesma ordem.
  const [title, setTitle] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [priority, setPriority] = useState<DemandPriority>('medium');
  const [objective, setObjective] = useState('');
  const [description, setDescription] = useState('');
  const [scopeIn, setScopeIn] = useState('');
  const [scopeOut, setScopeOut] = useState('');
  const [criteria, setCriteria] = useState('');
  const [runtime, setRuntime] = useState('raios');
  const [supervision, setSupervision] = useState('supervised');
  const [pipelineSlug, setPipelineSlug] = useState('');

  // Selects dinâmicos (RLS-filtered), carregados ao abrir.
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [pipelines, setPipelines] = useState<PipelineSummary[]>([]);

  useEffect(() => {
    if (!open) return;
    Promise.all([api.listBusinesses(), listPipelines()])
      .then(([bs, pls]) => {
        setBusinesses(bs);
        setPipelines(pls.filter((p) => p.is_active));
      })
      .catch((err) => console.error('NewDemandDialog: load selects failed', err));
  }, [open]);

  const selectedPipeline = pipelines.find((p) => p.slug === pipelineSlug);
  const requiresBusinessButMissing = !!selectedPipeline?.requires_business && !businessId;

  // title + description obrigatórios no backend (z.string().min(1)).
  // DEM-2010 AC#2: bloqueio SÓ em violações REAIS (title/description vazios,
  // business_id ausente quando o pipeline exige). Os demais campos do contrato
  // são DERIVADOS — não forçamos preenchimento manual (anti-fricção).
  const canSubmit =
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    !requiresBusinessButMissing;

  // DEM-2010 AC#2: PREVIEW dos campos do contrato que o usuário deixou em branco
  // e que serão derivados automaticamente (origem + backstop). Usa a MESMA
  // deriveDemandFields do backend (via espelho). Só calcula com material mínimo
  // disponível — vazio quando o usuário preencheu tudo.
  const derivedPreview =
    title.trim().length > 0 || description.trim().length > 0
      ? previewDerivedFields({
          title,
          description,
          objective,
          scopeIn,
          scopeOut,
          acceptanceCriteria: criteria
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean),
        })
      : [];

  function reset() {
    setTitle('');
    setBusinessId('');
    setPriority('medium');
    setObjective('');
    setDescription('');
    setScopeIn('');
    setScopeOut('');
    setCriteria('');
    setRuntime('raios');
    setSupervision('supervised');
    setPipelineSlug('');
    setError(null);
  }

  async function handleSubmit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Critérios de aceite: um por linha (wireframe "Um por linha").
      const acceptanceCriteria = criteria
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);

      // Campos aceitos pelo backend mas fora da assinatura TS de
      // api.createDemand (intocável). Spread não sofre excess-property check.
      const extraFields = {
        ...(objective.trim() ? { objective: objective.trim() } : {}),
        ...(scopeIn.trim() ? { scope_in: scopeIn.trim() } : {}),
        ...(scopeOut.trim() ? { scope_out: scopeOut.trim() } : {}),
        ...(acceptanceCriteria.length > 0 ? { acceptance_criteria: acceptanceCriteria } : {}),
        runtime_target: runtime,
        supervision_mode: supervision,
      };

      await api.createDemand({
        title: title.trim(),
        description: description.trim(),
        priority,
        // creator_context obrigatório (CHECK demands_creator_context_canonical);
        // wireframe não expõe o campo → derivado do form.
        creator_context: `Criada manualmente via UI (tela Demandas). Objetivo: ${
          objective.trim() || title.trim()
        }`.slice(0, 2000),
        ...(businessId ? { business_id: businessId } : {}),
        ...(pipelineSlug ? { pipeline_slug: pipelineSlug } : {}),
        ...extraFields,
      });
      reset();
      setOpen(false);
      onCreated?.();
    } catch (err) {
      console.error('Failed to create demand:', err);
      setError(err instanceof Error ? err.message : 'Falha ao criar demanda');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {/* Botão "Nova demanda" do wireframe (.dnewbtn) — margin-left:auto
            fecha a linha de filtros à direita (posição exata do mockup, C1). */}
        <button type="button" className="wf-dnewbtn">
          <Plus className="ic" aria-hidden="true" />
          Nova demanda
        </button>
      </DialogTrigger>
      <DialogContent
        size="lg"
        hideClose
        className="max-w-[760px] w-[92vw] p-0 gap-0 overflow-hidden flex flex-col"
      >
        <DialogTitle className="sr-only">Nova demanda</DialogTitle>
        <DialogDescription className="sr-only">
          Criar nova demanda — o router decide o pipeline quando "auto".
        </DialogDescription>

        {/* Header fixo (wireframe .dl-h) */}
        <div className="wf-modal-header shrink-0">
          <ClipboardList className="ic" style={{ color: 'var(--wf-accent-text)' }} aria-hidden="true" />
          Nova demanda
          <button
            type="button"
            className="wf-modal-close"
            onClick={() => setOpen(false)}
            aria-label="Fechar"
          >
            <X className="ic" aria-hidden="true" />
          </button>
        </div>

        {/* Corpo rolável (wireframe .dl-b) */}
        <div className="wf-modal-body max-h-[65vh] overflow-y-auto">
          <div className="wf-field">
            <label className="wf-label" htmlFor="nd-title">Título</label>
            <input
              id="nd-title"
              className="wf-input"
              placeholder="Título curto e claro da demanda"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>

          <div className="wf-nc-2col">
            <div className="wf-field">
              <label className="wf-label" htmlFor="nd-business">
                Negócio
                {selectedPipeline?.requires_business && (
                  <span className="text-raios-danger-text normal-case ml-1">(obrigatório pelo pipeline)</span>
                )}
              </label>
              <select
                id="nd-business"
                className="wf-select"
                value={businessId}
                onChange={(e) => setBusinessId(e.target.value)}
              >
                <option value="">Nenhum</option>
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              {requiresBusinessButMissing && (
                <p className="text-xs text-raios-danger-text mt-1">
                  O pipeline "{selectedPipeline?.name}" exige negócio (requires_business=true).
                </p>
              )}
            </div>
            <div className="wf-field">
              <label className="wf-label" htmlFor="nd-priority">Prioridade</label>
              <select
                id="nd-priority"
                className="wf-select"
                value={priority}
                onChange={(e) => setPriority(e.target.value as DemandPriority)}
              >
                {NEW_DEMAND_PRIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="wf-field">
            <label className="wf-label" htmlFor="nd-objective">Objetivo</label>
            <textarea
              id="nd-objective"
              className="wf-textarea"
              placeholder="O resultado esperado da demanda"
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
            />
          </div>

          <div className="wf-field">
            <label className="wf-label" htmlFor="nd-description">Descrição</label>
            <textarea
              id="nd-description"
              className="wf-textarea"
              placeholder="Resumo direto do que precisa ser feito"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="wf-nc-2col">
            <div className="wf-field">
              <label className="wf-label" htmlFor="nd-scope-in">Dentro do escopo</label>
              <textarea
                id="nd-scope-in"
                className="wf-textarea"
                placeholder="O que entra"
                value={scopeIn}
                onChange={(e) => setScopeIn(e.target.value)}
              />
            </div>
            <div className="wf-field">
              <label className="wf-label" htmlFor="nd-scope-out">Fora do escopo</label>
              <textarea
                id="nd-scope-out"
                className="wf-textarea"
                placeholder="O que fica de fora"
                value={scopeOut}
                onChange={(e) => setScopeOut(e.target.value)}
              />
            </div>
          </div>

          <div className="wf-field">
            <label className="wf-label" htmlFor="nd-criteria">Critérios de aceite</label>
            <textarea
              id="nd-criteria"
              className="wf-textarea"
              placeholder="Um por linha"
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
            />
          </div>

          <div className="wf-nc-3col">
            <div className="wf-field">
              <label className="wf-label" htmlFor="nd-runtime">Runtime</label>
              <select
                id="nd-runtime"
                className="wf-select"
                value={runtime}
                onChange={(e) => setRuntime(e.target.value)}
              >
                {NEW_DEMAND_RUNTIME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="wf-field">
              <label className="wf-label" htmlFor="nd-supervision">Supervisão</label>
              <select
                id="nd-supervision"
                className="wf-select"
                value={supervision}
                onChange={(e) => setSupervision(e.target.value)}
              >
                {NEW_DEMAND_SUPERVISION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="wf-field">
              <label className="wf-label" htmlFor="nd-pipeline">Pipeline</label>
              <select
                id="nd-pipeline"
                className="wf-select"
                value={pipelineSlug}
                onChange={(e) => setPipelineSlug(e.target.value)}
              >
                <option value="">auto (router decide)</option>
                {pipelines.map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.name}
                    {p.latest_version_label ? ` (v${p.latest_version_label})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* DEM-2010 AC#2: preview dos campos do contrato que serão DERIVADOS
              automaticamente (não preenchidos manualmente). Informativo —
              NÃO bloqueia a submissão. */}
          {derivedPreview.length > 0 && (
            <div
              className="text-xs text-raios-muted bg-raios-surface/40 border border-raios-border/60 rounded px-2 py-1.5"
              data-testid="derived-fields-preview"
            >
              <p className="font-medium text-raios-text mb-1">
                Campos do contrato preenchidos automaticamente:
              </p>
              <ul className="space-y-0.5">
                {derivedPreview.map((f) => (
                  <li key={f.key}>
                    <span className="font-medium">{f.label}:</span>{' '}
                    <span className="text-raios-muted">
                      {f.value.length > 120 ? `${f.value.slice(0, 120)}…` : f.value}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <p className="text-xs text-raios-danger-text bg-raios-danger/10 border border-raios-danger/30 rounded px-2 py-1.5">
              {error}
            </p>
          )}
        </div>

        {/* Footer fixo (wireframe .dl-f) */}
        <div className="wf-modal-footer shrink-0">
          <button
            type="button"
            className="wf-btn wf-btn-ghost"
            onClick={() => setOpen(false)}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="wf-btn wf-btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? 'Criando…' : 'Criar demanda'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
